import { useMemo, useState } from "react";
import { Badge, Button, EmptyState, QueryStateBoundary, Select } from "@/components/ui";
import { useReviewCount, useReviews } from "@/api/hooks/useReviews";
import { useEvent } from "@/api/hooks/useEvents";
import {
  REVIEW_PLATFORM_BADGE_VARIANT,
  REVIEW_PLATFORM_LABELS,
  ReviewPlatformSchema,
  type Review,
  type ReviewPlatform,
} from "@/api/types/review";
import { formatShortDate } from "@/features/events/eventDate";
import { LogReviewDialog } from "./LogReviewDialog";

/**
 * `received_at` is a full timestamp (mirrors payment.received_at in
 * InvoiceDetailPage.tsx's formatPaymentTimestamp — duplicated locally here
 * rather than centralized, matching that file's existing precedent).
 */
function formatReceivedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

type PlatformFilter = ReviewPlatform | "all";

const PLATFORM_OPTIONS = ReviewPlatformSchema.options;

/**
 * Phase 6's Reviews page: the BrightLocal-20 counter up top (server-
 * authoritative N/target from GET /v2/reviews/count — never hardcode 20
 * client-side, per the review-count-response contract), then the filterable
 * review log with a "Log review" dialog.
 */
export function ReviewsPage() {
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [logOpen, setLogOpen] = useState(false);

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useReviews({ platform: platformFilter === "all" ? undefined : platformFilter });

  const reviews = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Reviews</h1>
        <Button onClick={() => setLogOpen(true)}>Log review</Button>
      </div>

      <ReviewCounterCard />

      <div className="mb-4 mt-6 flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="review-platform-filter">
            Platform
          </label>
          <Select
            id="review-platform-filter"
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value as PlatformFilter)}
          >
            <option value="all">All platforms</option>
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {REVIEW_PLATFORM_LABELS[p]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {reviews.length === 0 ? (
          <EmptyState
            icon="⭐"
            title="No reviews logged yet"
            description="Log a review whenever a client leaves one — it counts toward the 20-review goal."
            action={{ label: "Log review", onClick: () => setLogOpen(true) }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-bg-card" data-testid="review-list">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Platform</th>
                  <th className="px-4 py-2 font-medium">Rating</th>
                  <th className="px-4 py-2 font-medium">Link</th>
                  <th className="px-4 py-2 font-medium">Received</th>
                  <th className="px-4 py-2 font-medium">Event</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reviews.map((review) => (
                  <ReviewRow key={review.id} review={review} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryStateBoundary>

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}

      <LogReviewDialog open={logOpen} onClose={() => setLogOpen(false)} />
    </div>
  );
}

// ---- Counter ---------------------------------------------------------------

function ReviewCounterCard() {
  const { data: count, isLoading } = useReviewCount();

  if (isLoading || !count) {
    return (
      <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="review-counter">
        <p className="text-sm text-text-muted">Loading review count...</p>
      </div>
    );
  }

  const atOrOverTarget = count.total >= count.target;
  const pct = count.target > 0 ? Math.min(100, Math.round((count.total / count.target) * 100)) : 0;

  return (
    <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="review-counter">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-text-secondary">Reviews toward the 20-review goal</p>
        <Badge variant={atOrOverTarget ? "success" : "secondary"} data-testid="review-counter-badge">
          {atOrOverTarget ? "Goal reached" : `${count.remaining} to go`}
        </Badge>
      </div>
      <p className="mt-1 font-display text-3xl font-bold text-text-primary" data-testid="review-counter-count">
        {count.total} <span className="text-lg font-normal text-text-muted">of {count.target}</span>
      </p>
      <div
        className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-bg-muted"
        role="progressbar"
        aria-valuenow={count.total}
        aria-valuemin={0}
        aria-valuemax={count.target}
        data-testid="review-counter-progress"
      >
        <div
          className={atOrOverTarget ? "h-full rounded-full bg-success" : "h-full rounded-full bg-copper"}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ---- Row --------------------------------------------------------------------

function stars(rating: number | null): string {
  if (rating === null) return "—";
  const full = Math.max(0, Math.min(5, Math.round(rating)));
  return "★".repeat(full) + "☆".repeat(5 - full);
}

function ReviewRow({ review }: { review: Review }) {
  const { data: event } = useEvent(review.event_id ?? undefined);

  return (
    <tr data-review-id={review.id}>
      <td className="px-4 py-3">
        <Badge variant={REVIEW_PLATFORM_BADGE_VARIANT[review.platform]}>
          {REVIEW_PLATFORM_LABELS[review.platform]}
        </Badge>
      </td>
      <td className="px-4 py-3 text-text-primary" aria-label={review.rating ? `${review.rating} stars` : "No rating"}>
        {stars(review.rating)}
      </td>
      <td className="px-4 py-3">
        <a
          href={review.url}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary hover:underline"
        >
          Open
        </a>
      </td>
      <td className="px-4 py-3 text-text-secondary">{formatReceivedAt(review.received_at)}</td>
      <td className="px-4 py-3 text-text-secondary">
        {review.event_id ? (event ? `${formatShortDate(event.event_date)}${event.venue_name ? ` — ${event.venue_name}` : ""}` : "…") : "—"}
      </td>
    </tr>
  );
}
